// Public reviews list page + carousel widget for the home page
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { API } from "../lib/providers";
import { Star, ShieldCheck, Camera, X, CornerDownRight } from "lucide-react";
import { useSettings } from "../lib/providers";

const TENNIS_GREEN = "#a7ff00";

function StarRow({ n, size = 14 }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className="inline-block" style={{ width: size, height: size }} fill={i <= n ? TENNIS_GREEN : "none"} color={i <= n ? TENNIS_GREEN : "#666"} />
      ))}
    </div>
  );
}

// ─────────────────────────── PUBLIC /avis PAGE ───────────────────────────
export default function ReviewsPublicPage() {
  const { settings } = useSettings();
  const shopName = settings?.site_name || "DZ Tennis Shopper";
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ count: 0, avg: 0, dist: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 } });
  const [filter, setFilter] = useState({ rating: 0, withPhotos: false });
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null); // {url}

  useEffect(() => {
    (async () => {
      try {
        const [r, s] = await Promise.all([
          axios.get(`${API}/reviews/public?limit=200`),
          axios.get(`${API}/reviews/public/stats`),
        ]);
        setReviews(r.data.data || []);
        setStats(s.data || stats);
      } catch (e) { /* ignore */ }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, []);

  const filtered = useMemo(() => {
    return reviews.filter((r) => {
      if (filter.rating && r.rating !== filter.rating) return false;
      if (filter.withPhotos && (!r.photos || r.photos.length === 0)) return false;
      return true;
    });
  }, [reviews, filter]);

  return (
    <div className="min-h-screen py-12 px-4 max-w-5xl mx-auto" data-testid="reviews-public-page">
      <header className="text-center mb-10">
        <h1 className="text-4xl sm:text-5xl font-black tracking-tight">Ce que disent nos clients</h1>
        <p className="text-white/60 text-sm mt-2">Avis vérifiés des clients ayant reçu leur commande.</p>
      </header>

      {/* Stats */}
      <div className="border border-white/10 p-6 mb-8" data-testid="reviews-public-stats">
        <div className="flex flex-col md:flex-row items-center gap-6">
          <div className="text-center">
            <div className="text-5xl font-black text-neon">{stats.avg.toFixed(1)}</div>
            <StarRow n={Math.round(stats.avg)} size={20} />
            <div className="text-xs text-white/50 mt-1">{stats.count} avis</div>
          </div>
          <div className="flex-1 w-full">
            {[5, 4, 3, 2, 1].map((s) => {
              const c = stats.dist[s] || 0;
              const pct = stats.count ? (c / stats.count) * 100 : 0;
              return (
                <button key={s} onClick={() => setFilter({ ...filter, rating: filter.rating === s ? 0 : s })} className={`w-full flex items-center gap-2 py-1 text-left ${filter.rating === s ? "opacity-100" : "opacity-70 hover:opacity-100"}`} data-testid={`reviews-stat-${s}`}>
                  <span className="text-xs w-8">{s} ⭐</span>
                  <div className="flex-1 h-2 bg-white/5">
                    <div className="h-full bg-neon" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-xs text-white/50 w-8 text-right">{c}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="reviews-public-filters">
        <button onClick={() => setFilter({ rating: 0, withPhotos: false })} className={`px-3 py-1.5 text-xs uppercase tracking-widest ${filter.rating === 0 && !filter.withPhotos ? "bg-neon text-black font-bold" : "bg-white/5 hover:bg-white/10"}`}>Tous</button>
        <button onClick={() => setFilter({ ...filter, withPhotos: !filter.withPhotos })} className={`px-3 py-1.5 text-xs uppercase tracking-widest flex items-center gap-1 ${filter.withPhotos ? "bg-neon text-black font-bold" : "bg-white/5 hover:bg-white/10"}`} data-testid="reviews-public-photos-filter">
          <Camera className="w-3 h-3" /> Avec photos
        </button>
      </div>

      {/* Reviews list */}
      {loading && <div className="text-center text-white/40">Chargement…</div>}
      {!loading && filtered.length === 0 && (
        <div className="text-center py-16 border border-white/10 bg-white/[0.02]" data-testid="reviews-list-empty">
          <div className="text-6xl mb-4">🎾</div>
          <h3 className="text-xl font-black uppercase tracking-widest mb-2">
            {stats.count === 0 ? "Soyez le premier à laisser un avis" : "Aucun avis pour ces filtres"}
          </h3>
          <p className="text-sm text-white/60 max-w-md mx-auto mb-6">
            {stats.count === 0
              ? `Nos premiers clients recevront très bientôt leur commande. Vous serez notifié après réception pour partager votre expérience avec la communauté ${shopName}.`
              : "Réinitialisez les filtres pour voir tous les avis disponibles."}
          </p>
          {stats.count === 0 ? (
            <a
              href="#products"
              className="inline-block px-6 py-3 bg-neon text-black text-xs font-black uppercase tracking-widest hover:opacity-90 transition-opacity"
              data-testid="reviews-empty-cta"
            >
              Voir le catalogue
            </a>
          ) : (
            <button
              onClick={() => setFilter({ rating: 0, withPhotos: false })}
              className="inline-block px-6 py-3 bg-neon text-black text-xs font-black uppercase tracking-widest hover:opacity-90 transition-opacity"
              data-testid="reviews-empty-reset"
            >
              Réinitialiser les filtres
            </button>
          )}
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-4" data-testid="reviews-public-list">
        {filtered.map((r) => (
          <div key={r.id} className="border border-white/10 p-4 hover:border-neon/30 transition-colors" data-testid={`reviews-public-card-${r.id}`}>
            <div className="flex items-center justify-between mb-2">
              <StarRow n={r.rating} size={16} />
              <span className="inline-flex items-center gap-1 text-[10px] text-neon uppercase tracking-widest font-bold">
                <ShieldCheck className="w-3 h-3" /> Achat vérifié
              </span>
            </div>
            <p className="text-sm text-white/85">{r.comment || <em className="text-white/40">Pas de commentaire.</em>}</p>
            {r.photos && r.photos.length > 0 && (
              <div className="flex gap-1.5 mt-3 flex-wrap">
                {r.photos.map((url, i) => (
                  <button key={i} onClick={() => setLightbox({ url })} className="w-16 h-16 overflow-hidden border border-white/10 hover:border-neon">
                    <img src={url} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            <div className="text-[10px] text-white/40 mt-3 font-mono">— {r.customer_name} · {(r.created_at || "").slice(0, 10)}</div>

            {/* Admin public reply */}
            {r.admin_reply && r.admin_reply.trim() && (
              <div className="mt-3 pt-3 border-t border-neon/20" data-testid={`review-admin-reply-${r.id}`}>
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-neon font-bold mb-1">
                  <CornerDownRight className="w-3 h-3" /> Réponse du gérant — {shopName}
                </div>
                <p className="text-xs text-white/85 leading-relaxed whitespace-pre-line">{r.admin_reply}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="reviews-lightbox">
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 text-white/80 hover:text-white"><X className="w-8 h-8" /></button>
          <img src={lightbox.url} alt="" className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </div>
  );
}

// ─────────────────────────── HOME CAROUSEL WIDGET ───────────────────────────
export function ReviewsCarousel() {
  const [reviews, setReviews] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    axios.get(`${API}/reviews/public?limit=10&min_rating=5`).then((r) => setReviews(r.data.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (reviews.length === 0) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % reviews.length), 6000);
    return () => clearInterval(t);
  }, [reviews.length]);

  if (reviews.length === 0) return null;
  const visible = reviews.slice(idx, idx + 3).concat(reviews.slice(0, Math.max(0, 3 - (reviews.length - idx))));

  return (
    <section className="py-12 px-4 max-w-6xl mx-auto" data-testid="reviews-carousel">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight">Avis clients ⭐</h2>
        <a href="/avis" className="text-xs uppercase tracking-widest text-neon hover:underline" data-testid="reviews-carousel-link">Voir tous les avis →</a>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {visible.slice(0, 3).map((r, i) => (
          <div key={`${r.id}-${i}`} className="border border-white/10 p-4 bg-white/5">
            <StarRow n={r.rating} size={16} />
            <p className="text-sm text-white/85 mt-2 line-clamp-4">{r.comment}</p>
            {r.photos && r.photos.length > 0 && (
              <img src={r.photos[0]} alt="" className="w-full h-32 object-cover mt-3 border border-white/10" />
            )}
            <div className="flex items-center justify-between mt-3">
              <span className="text-[10px] text-white/50 font-mono">— {r.customer_name}</span>
              <span className="inline-flex items-center gap-1 text-[9px] text-neon uppercase tracking-widest"><ShieldCheck className="w-3 h-3" /> Vérifié</span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
